import { acceptHMRUpdate, defineStore, storeToRefs } from "pinia";
import { computed, ref } from "vue";
import { DateTime } from "luxon";

import LocalForagePlugin, { syncState } from "@/plugins/LocalForagePlugin";
import { sectorHistory } from "@/plugins/LocalForagePlugin";

import { useApplicationStore } from "@/stores/Application";
import { useAuthStore } from "@/stores/Auth";
import {
    calculateMissingIdxDeltas,
    hydrate,
    isBullishDirection,
    isBullishSignal,
    refreshFromServer
} from "@/services/data/sync/sector-history-sync-coordinator";
import { Unauthorized } from "@tmi/types/api/responses";

import type { Tuple } from "@tmi/types/adts";
import type { CompositionContext } from "@/services/data/sync-supervisor";
import {
    BpiStatus,
    PersistedSectorsState,
    SectorAlert,
    SectorMarketState,
    SectorMarketStateContainer,
    SectorMarketStateValues
} from "@tmi/types/entities";
import { formatDateAsSortableString, fromSortableString, normalizeDateTime } from "@tmi/utils/date-time";

// Non-reactive variables
import { consoleLoggerFactory, getDefaultLoggerOptions, ILogger, LogLevels } from "@/types/console-logger";
const logger: ILogger = consoleLoggerFactory.create( getDefaultLoggerOptions( LogLevels.ERROR ) );

import { clientStateManager } from "@/services/error-services";
import { useLocalStorage } from "@vueuse/core";

const DEFAULT_TIMEOUT_MS = 30000; // 30 seconds

function withTimeout<T>( promise: Promise<T>, timeoutMs: number = DEFAULT_TIMEOUT_MS, operationName: string = 'Operation' ): Promise<T> {
    return new Promise<T>( ( resolve, reject ) => {
        const timeoutId = setTimeout( () => {
            reject( new Error( `${ operationName } timed out after ${ timeoutMs }ms` ) );
        }, timeoutMs );

        promise
            .then( ( result ) => {
                clearTimeout( timeoutId );
                resolve( result );
            } )
            .catch( ( err ) => {
                clearTimeout( timeoutId );
                reject( err );
            } );
    } );
}

const error = ref< string | undefined >();
const initialized = ref< boolean >( false );
const fetching = ref< boolean >( false );
const loading = ref< boolean >( false );

const isLoading = computed< boolean >( () => loading.value );

const sectorHistoryRepository =
    ref< Map< string, Map< number, SectorMarketState > > >( new Map< string, Map< number, SectorMarketState > >() );

const allSectorAlertsUniquenessTracker = new Set< string >();
const allSectorAlerts = ref< SectorAlert[] >( [] );

const selectedDate = ref< string >( '' );

const dateOfMostRecentMarketDataAvailable = useLocalStorage< string >( 'latest-dt', '' );

type PointAndFigureHistoricalContext = {
    previousFieldPosition: number,
    previousColumn: string,
    previousSignal: string
}

// Local variables used to define the current BPI Status value
const sectorPnfHistoricalContextMap = new Map< number, PointAndFigureHistoricalContext >();

export const useSectorHistoryStore = defineStore( "sector-history", () => {
    // Depends on authStore for authentication state and appStore to signal that data is being loaded
    const appStore = useApplicationStore();
    const { authToken } = storeToRefs( useAuthStore() );

    // const { dateOfMostRecentMarketDataAvailable } = storeToRefs( appStore );

    const latestMarketDataRetrieved = computed< string | undefined >( () => {
        const dates = [ ...( Array.from( sectorHistoryRepository.value.keys() ) ) ]
            .sort( ( d1: string, d2: string ) => d2.localeCompare( d1 ) );

        return dates.length > 0 ? dates[ 0 ] : undefined;
    } );

    // Clear all internal tracking and state variables
    function reset() {
        error.value = undefined;
        initialized.value = false;
        loading.value = false;

        resetSectorHistory();
    }

    async function initializeAsync(): Promise<void> {
        if ( initialized.value || loading.value ) {
            logger.debug( '[ Sector-History ].initialize() exiting - already processing...' );
            return;
        }

        loading.value = true;

        try {
            // Initialize the "current" selected date based on what is available within the Database
            selectedDate.value = dateOfMostRecentMarketDataAvailable.value;

            // Wait for the driver to settle
            if ( typeof sectorHistory === 'undefined' || typeof syncState === 'undefined' ) {
                LocalForagePlugin.initialize();
            }

            await withTimeout( sectorHistory.ready(), DEFAULT_TIMEOUT_MS, 'IndexedDB ready' );

            // Query IDB for the number of sector history records that already exist in the database
            const len = await sectorHistory.length();

            if ( len > 0 ) {
                // Clear the contents of the collections
                sectorHistoryRepository.value = new Map<string, Map<number, SectorMarketState>>();

                // Iterate over all elements within IDB
                const loadedStateData: PersistedSectorsState[] = [];
                await sectorHistory.iterate( ( history: PersistedSectorsState ) => {
                    loadedStateData.push( history );
                    return;
                } );

                // Reset all variables used to track BPI Status
                sectorPnfHistoricalContextMap.clear();

                buildReactiveReferences( loadedStateData );
                initialized.value = true;
            } else {
                // Initial "starting point"
                const dateOfLatestMarketDataReceived: string | undefined = latestMarketDataRetrieved.value;
                const latestRetrievedUtcDate: DateTime = dateOfLatestMarketDataReceived
                    ? normalizeDateTime( dateOfLatestMarketDataReceived )!.plus( { days: -30 } )
                    : fromSortableString( appStore.dateOfMostRecentMarketDataAvailable );

                if ( authToken.value && latestMarketDataRetrieved.value ) {
                    const missingDates = [ formatDateAsSortableString( latestRetrievedUtcDate ) ];

                    const sectorStateData = await withTimeout(
                        refreshFromServer( authToken.value, appStore.compositionCtx, missingDates ),
                        DEFAULT_TIMEOUT_MS,
                        'Sector history fetch'
                    );

                    buildReactiveReferences( sectorStateData );
                    initialized.value = true;
                } else {
                    // No auth token or no latest data available — mark as initialized anyway to prevent stuck state
                    logger.debug( '[ Sector-History ].initialize() - No auth token or latest data, marking initialized' );
                    initialized.value = true;
                }
            }
        } catch ( err: any ) {
            reset();
            clientStateManager.queueClientError( err, {
                token: authToken.value
            } );
        } finally {
            loading.value = false;
        }
    }

    function buildReactiveReferences( sectorStateData: PersistedSectorsState[] ): void {
        calculateMissingIdxDeltas( sectorStateData );

        let alerts: SectorAlert[] = [];
        sectorStateData.forEach( ( state: PersistedSectorsState ) => {
            state.sectors.forEach( ( sectorState: SectorMarketState ) => {
                if ( sectorState.alerts && sectorState.alerts.length > 0 ) {
                    alerts = [ ...sectorState.alerts, ...alerts ];
                }

                sectorState.bpi.stat = calculateBpiStatus( sectorState.sector.id, sectorState.bpi.col?.toLocaleLowerCase(), sectorState.bpi.sig?.toLocaleLowerCase(), sectorState.bpi.val );

                sectorHistoryRepository.value.set( state.dt, new Map< number, SectorMarketState >( state.sectors.map( state => [ state.sector.id, state ] ) ) )
            });
        } );

        alerts.sort( ( sa1: SectorAlert, sa2: SectorAlert ) => sa2.dt.localeCompare( sa1.dt ) );
        allSectorAlerts.value = [ ...alerts ];
    }

    const sectorStateForSelectedDate = computed< SectorMarketStateContainer >( () => {
        let sectorData: SectorMarketState[] = [];

        const currentlySelectedDate = selectedDate.value;
        if ( !!currentlySelectedDate && sectorHistoryRepository.value.has( currentlySelectedDate ) ) {
            const repositoryDateForSelectedDate: Map< number, SectorMarketState > | undefined = sectorHistoryRepository.value.get( currentlySelectedDate );
            if ( repositoryDateForSelectedDate ) {
                sectorData = [ ...( Array.from( repositoryDateForSelectedDate.values() ) ) ];
            }
        }

        return {
            date: selectedDate.value,
            history: sectorData
        };
    } );

    const sectorStateValues = computed< Map< number, SectorMarketStateValues[] > >( () => {
        const allSectorsMetricHistory = new Map< number, SectorMarketStateValues[] >();
        const ewIndexLatestValueBySectorId = new Map< number, number | null | undefined >();

        const mostRecentValidMarketDate: DateTime = normalizeDateTime( appStore.dateOfMostRecentMarketDataAvailable );
        if ( mostRecentValidMarketDate.isValid ) {
            const availableDates = [ ...sectorHistoryRepository.value.keys() ].filter( date => {
                const dateOfRecord = normalizeDateTime( date );
                return dateOfRecord.isValid;
            } );

            const initialDate = normalizeDateTime( '2018-01-01' );
            availableDates.forEach( dt => {
                const metricDataForAllSectorsOnDate = sectorHistoryRepository.value.get( dt );
                if ( !!metricDataForAllSectorsOnDate ) {
                    for ( let entry of Array.from( metricDataForAllSectorsOnDate.entries() ) ) {
                        const sectorId: number = entry[ 0 ];

                        // Verify the history array has been initialized
                        if ( !allSectorsMetricHistory.has( sectorId ) ) {
                            allSectorsMetricHistory.set( sectorId, [] );
                        }

                        const sectorMetricsForDate = metricDataForAllSectorsOnDate.get( sectorId );
                        if ( !sectorMetricsForDate ) {
                            logger.error( `Sector data for ${ sectorId } is not defined for ${ dt }` );
                            continue;
                        }

                        const latestEquallyWeightedIndexValueForSector = ewIndexLatestValueBySectorId.get( sectorId );

                        // We look back One Year + 1 day, to have a delta for each data point, therefore, if this is not defined,
                        // store the current value based on the date under review and then return
                        if ( !latestEquallyWeightedIndexValueForSector ) {
                            ewIndexLatestValueBySectorId.set( sectorId, sectorMetricsForDate.idx?.val );
                            continue;
                        }

                        // TODO: perform the same sort of "post-mortem" calculation for BPI Status here

                        const idx = sectorMetricsForDate.idx!;
                        if ( normalizeDateTime( sectorMetricsForDate.dt ) > initialDate ) {
                            allSectorsMetricHistory.get( sectorId )?.push( {
                                dt: sectorMetricsForDate.dt,
                                idx: {
                                    val: idx.val,
                                    delta: idx.val - latestEquallyWeightedIndexValueForSector,
                                    col: idx.col,
                                    col_chg_dt: idx.col_chg_dt,
                                    sig: idx.sig,
                                    sig_chg_dt: idx.sig_chg_dt
                                },
                                bpi: sectorMetricsForDate.bpi,
                                p30: sectorMetricsForDate.p30,
                                p10: sectorMetricsForDate.p10,
                                rs: sectorMetricsForDate.rs,
                                alerts: sectorMetricsForDate.alerts
                            } );
                        }
                    }
                }
            } );
        }

        return allSectorsMetricHistory;
    } );

    async function setSelectedDate( selectedDateAsSortableString: string ): Promise<void> {
        await initializeAsync();

        if ( !selectedDateAsSortableString ) {
            return;
        }

        logger.debug( `selectedDateAsSortableString: ${ selectedDateAsSortableString }, alreadyAvailable: ${ sectorHistoryRepository.value.has( selectedDateAsSortableString ) }` );

        if ( !authToken.value ) {
            throw new Unauthorized();
        }

        if ( sectorHistoryRepository.value.has( selectedDateAsSortableString ) ) {
            // Data already loaded
            selectedDate.value = selectedDateAsSortableString;
            return;
        }

        fetching.value = true;
        logger.debug( `Date ${ selectedDateAsSortableString } is not currently in memory, fetching...` );

        try {
            const sectorHistoryData = await withTimeout(
                refreshFromServer( authToken.value, appStore.compositionCtx, [ selectedDateAsSortableString ] ),
                DEFAULT_TIMEOUT_MS,
                'Sector history fetch for date'
            );

            if ( sectorHistoryData?.length > 0 ) {
                for ( const marketData of sectorHistoryData ) {
                    hydrateFromContainer( marketData, appStore.compositionCtx );
                }
            }
        } catch ( err: any ) {
            reset();
            clientStateManager.queueClientError( err, {
                token: authToken.value,
                selectedDate: selectedDateAsSortableString
            } );
        } finally {
            selectedDate.value = selectedDateAsSortableString;
            fetching.value = false;
        }
    }

    const mostRecentSectorData = computed< SectorMarketState[] >( () => {
        let latestSectorHistory: SectorMarketState[] = [];

        if ( sectorHistoryRepository.value.size > 0 ) {
            const datesInMemory = [ ...sectorHistoryRepository.value.keys() ]
                .sort( ( d1: string, d2: string ): number => d2.localeCompare( d1 ) );

            const mostRecentDate: string = datesInMemory[ 0 ];
            latestSectorHistory = [ ...( Array.from( sectorHistoryRepository.value.get( mostRecentDate )!.values() ) ) ];
        }

        return latestSectorHistory;
    } );

    function resetSectorHistory(): void {
        sectorHistoryRepository.value?.clear();
    }

    function hydrateFromContainer( entity: PersistedSectorsState, ctx: CompositionContext ): void {
        if ( !( allSectorAlerts.value ) ) {
            allSectorAlerts.value = [];
            allSectorAlertsUniquenessTracker.clear();
        }

        if ( !( sectorHistoryRepository.value ) ) {
            sectorHistoryRepository.value = new Map< string, Map< number, SectorMarketState > >();
        }

        const result: Tuple< string, Map< number, SectorMarketState > > = hydrate( entity, ctx );

        // Calculate missing idx deltas using the previous date's data already in the repository
        const previousDate = [ ...sectorHistoryRepository.value.keys() ]
            .filter( dt => dt < result.item1 )
            .sort( ( a, b ) => b.localeCompare( a ) )[ 0 ];

        if ( previousDate ) {
            const previousDateData = sectorHistoryRepository.value.get( previousDate );
            if ( previousDateData ) {
                for ( const [ sectorId, sectorState ] of result.item2 ) {
                    if ( sectorState.idx && sectorState.idx.delta == null ) {
                        const previousSectorState = previousDateData.get( sectorId );
                        if ( previousSectorState?.idx ) {
                            sectorState.idx.delta = sectorState.idx.val - previousSectorState.idx.val;
                        }
                    }
                }
            }
        }

        sectorHistoryRepository.value.set( result.item1, result.item2 );
    }

    const OverboughtThreshold = 70.0;
    const OversoldThreshold = 30.0;

    function calculateBpiStatus( sectorId: number, column: string | null, signal: string | null, currentFieldPosition: number ): string | null {
        let status = null;

        const pnfHistoricalContextForSector = sectorPnfHistoricalContextMap.has( sectorId ) ? sectorPnfHistoricalContextMap.get( sectorId ) : null;
        if ( !pnfHistoricalContextForSector ) {
            // Don't start tracking these values for BPI status until there is a full complement
            if ( currentFieldPosition && column && signal ) {
                sectorPnfHistoricalContextMap.set( sectorId, {
                    previousFieldPosition: currentFieldPosition,
                    previousColumn: column,
                    previousSignal: signal
                } );
            }

            return status;
        }

        const currentColumn = column!;
        const currentSignal = signal!;

        // To define the BPI status, the column, signal, and field position must be defined
        if ( currentFieldPosition > 0.0 ) {
            if ( isBullishSignal( currentSignal ) ) {
                if ( !isBullishDirection( currentColumn ) ) {
                    const previousFieldPosition = pnfHistoricalContextForSector.previousFieldPosition;
                    if ( previousFieldPosition > OverboughtThreshold && currentFieldPosition < OverboughtThreshold ) {
                        // 4 - Bear Alert: Chart is falling from above 70% to below 70% without yet generating a p&f sell.
                        status = BpiStatus.BearAlert;
                    } else if ( currentFieldPosition > OverboughtThreshold ) {
                        // 7 - Bull Top: The chart is falling (column of O's) but is still above 70%.
                        status = BpiStatus.BullTop;
                    } else {
                        // 1 - Bull Correction: Chart is on a p&f buy signal but is falling (column of O's) without yet reaching 70%.
                        status = BpiStatus.BullCorrection;
                    }
                }
                else { // isBullishDirection
                    // 2 - Bull Confirmed: Chart is on a p&f buy signal and is rising (column of X's); and/or is in a column of X's above 68%.
                    status = BpiStatus.BullConfirmed;
                }
            } else { // isBearishSignal
                if ( isBullishDirection( currentColumn ) ) {
                    const previousFieldPosition = pnfHistoricalContextForSector.previousFieldPosition;
                    // 3 - Bull Alert: Chart rising (X's) moving up from below 30% but has not yet generated a p&f buy.
                    if ( previousFieldPosition < OversoldThreshold && currentFieldPosition > OversoldThreshold ) {
                        status = BpiStatus.BullAlert;
                    } else if ( currentFieldPosition < OverboughtThreshold ) {
                        // 6 - Bear Correction: Chart is on a p&f sell but is rising (column X's) without having moved above 68%.
                        status = BpiStatus.BearCorrection;
                    } else {
                        // 2 - Bull Confirmed: Chart is on a p&f buy signal and is rising (column of X's); and/or is in a column of X's above 68%.
                        status = BpiStatus.BullConfirmed;
                    }
                } else { // isBearishColumn
                    // 5 - Bear Confirmed: Chart is falling (Column O's) below 70% and has generated a p&f sell.
                    if ( currentFieldPosition < OverboughtThreshold ) {
                        status = BpiStatus.BearConfirmed;
                    }
                }
            }
        }

        // Update the most recent context values used for this sector
        if ( currentFieldPosition && column && signal ) {
            sectorPnfHistoricalContextMap.set( sectorId, {
                previousFieldPosition: currentFieldPosition,
                previousColumn: column,
                previousSignal: signal
            } );
        }

        return status;
    }

    return {
        // ACTIONS
        initializeAsync,
        reset,
        setSelectedDate,

        // GETTERS
        allSectorAlerts,
        error,
        isLoading,
        mostRecentSectorData,
        sectorStateForSelectedDate,
        sectorStateValues
    }
} );

if ( import.meta.hot ) {
    import.meta.hot.accept( acceptHMRUpdate( useSectorHistoryStore, import.meta.hot ) );
}
